import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, ChevronRight, CheckCircle, ArrowRight, Filter,
  Key, Users, Laptop, FileText, Cloud, Shield, DollarSign, Layers
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';
import { integrations, categories } from '../../data/integrations';
import { getLogoByIntegrationId } from '../../constants/logos';

const categoryIcons = {
  'IdP': Key,
  'HRIS': Users,
  'MDM': Laptop,
  'ITSM': FileText,
  'SaaS': Layers,
  'Security': Shield,
  'Cloud': Cloud,
  'Finance': DollarSign
};

export const IntegrationsDirectory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const filteredIntegrations = useMemo(() => {
    return integrations.filter(integration => {
      const matchesSearch = searchQuery === '' ||
        integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        integration.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = selectedCategory === 'all' ||
        integration.category === selectedCategory;

      const matchesStatus = selectedStatus === 'all' ||
        integration.status === selectedStatus;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [searchQuery, selectedCategory, selectedStatus]);

  const integrationCounts = useMemo(() => {
    const counts = { all: integrations.length };
    categories.forEach(cat => {
      counts[cat.id] = integrations.filter(i => i.category === cat.id).length;
    });
    return counts;
  }, []);

  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
              Integrations
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl leading-relaxed">
              Connect to 50+ systems including Identity Providers, HRIS, MDM, ITSM, and SaaS applications.
              Read-only by default. Write operations require explicit policy enablement.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="default">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="default" variant="outline">
                <Link to="/integrations/sdk">Connector SDK</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Search and Filters */}
        <section className="py-8 px-6 bg-secondary/30 border-b border-border">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search integrations..."
                  className="w-full h-10 pl-9 pr-4 text-sm bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="h-10 px-3 text-sm bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="all">All Categories ({integrationCounts.all})</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.label} ({integrationCounts[cat.id]})
                    </option>
                  ))}
                </select>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="h-10 px-3 text-sm bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="all">All Statuses</option>
                  <option value="available">Available</option>
                  <option value="beta">Beta</option>
                  <option value="planned">Planned</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Category Pills */}
        <section className="py-6 px-6 bg-white border-b border-border">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${selectedCategory === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-foreground hover:bg-secondary/80'
                  }`}
              >
                All ({integrationCounts.all})
              </button>
              {categories.map(cat => {
                const Icon = categoryIcons[cat.id];
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${selectedCategory === cat.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-foreground hover:bg-secondary/80'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.label} ({integrationCounts[cat.id]})
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Integrations Grid */}
        <section className="py-12 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            {filteredIntegrations.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <Search className="w-6 h-6 text-muted-foreground" />
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2">No integrations found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Try adjusting your search or filter criteria.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                    setSelectedStatus('all');
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <>
                <div className="text-sm text-muted-foreground mb-6">
                  Showing {filteredIntegrations.length} integration{filteredIntegrations.length !== 1 ? 's' : ''}
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredIntegrations.map((integration) => {
                    const CategoryIcon = categoryIcons[integration.category];
                    const logo = getLogoByIntegrationId(integration.id);

                    return (
                      <Link
                        key={integration.id}
                        to={`/integrations/${integration.id}`}
                        className="group block bg-white border border-border rounded-lg p-5 hover:border-primary/50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                              {logo ? (
                                <img
                                  src={logo.src}
                                  alt={logo.alt}
                                  className="w-6 h-6 object-contain"
                                />
                              ) : (
                                <CategoryIcon className="w-5 h-5 text-primary" />
                              )}
                            </div>
                            <div>
                              <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                                {integration.name}
                              </h3>
                              <span className="text-xs text-muted-foreground">{integration.category}</span>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${integration.status === 'available' ? 'bg-green-100 text-green-700' :
                            integration.status === 'beta' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                            {integration.status.charAt(0).toUpperCase() + integration.status.slice(1)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                          {integration.description}
                        </p>
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Read:</span>
                            <span className="text-foreground">{integration.capabilities.read.length} objects</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Write:</span>
                            <span className="text-foreground">{integration.capabilities.write.length} actions</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </section>

        {/* Request Connector */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Don't See Your System?</h2>
            <p className="text-muted-foreground mb-8">
              Request a connector for any system with an API. Or build your own with the Connector SDK.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="default">
                <Link to="/integrations/request">Request Connector</Link>
              </Button>
              <Button asChild size="default" variant="outline">
                <Link to="/integrations/sdk">Connector SDK</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default IntegrationsDirectory;
